import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Icon } from '@syneto/compass-react';
import moment from 'moment';
import { ServiceContext } from '../../../contexts/ServiceContext';
import { NotificationContext } from '../../../contexts/NotificationContext';
import { ACCESS_REQUEST_STATUS } from '../../../services/PartnerPreview';

const REQUEST_ACCESS_FAILED_MESSAGE = 'Failed to send your Partner Preview access request. Please try again.';
const ALREADY_ENROLLED_MESSAGE = 'Your company already has access to the Partner Preview Program.';
const DATE_FORMAT = 'DD/MM/YYYY';

const renderEnrolledBanner = () => {
	return (
		<Alert variant="info" className="mb-3" style={{ backgroundColor: '#edeffb', borderColor: '#ce93d8', color: '#000' }}>
			<div className="d-flex align-items-start">
				<Icon name="fas fa-info-circle" size="lg" className="me-3 mt-1" />
				<div>
					<div style={{ fontWeight: 'bold' }}>Your company is enrolled in the Partner Preview Program.</div>
					<div>You can enable access to Preview updates for each registered machine from the side pane. Minimum required version: 6.0.5.</div>
				</div>
			</div>
		</Alert>
	);
};

// The persistent Partner Preview banner of the Inventory page (SYN-3072). Not dismissible. One of
// three mutually exclusive states:
//   1. not enrolled, nobody has requested -> invitation + "Request access"
//   2. a request is pending               -> who asked, when, + disabled "Access requested"
//   3. enrolled                           -> the SYN-2667 banner, unchanged
// Enrollment is decided by the participation the Inventory page already loads (`participating`);
// the access request read only decides between 1 and 2, and can itself report "enrolled" (a company
// enrolled since the participation read, or a request that comes back enrolled), which is state 3.
export const PartnerPreviewProgramBanner = (props) => {
	const { participating, participationLoaded, onEnrolled } = props;
	const { partnerPreviewService } = useContext(ServiceContext);
	const { pushDangerNotification, pushSuccessfulNotification } = useContext(NotificationContext);

	const [loading, setLoading] = useState(true);
	const [accessRequest, setAccessRequest] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		// Nothing to ask about a company that participates, and nothing safe to show before the
		// participation is known - an enrolled company must never flash the invitation.
		if (!participationLoaded || participating) {
			return;
		}

		let cancelled = false;

		const loadAccessRequest = async () => {
			try {
				const loadedAccessRequest = await partnerPreviewService.getAccessRequest();
				setAccessRequest(loadedAccessRequest);

				// The participation said "not participating" (a failed or lagging read) yet the
				// company is enrolled: have the page re-read it, so the side-pane toggle, the Version
				// Type column and the row icons match the enrolled banner. Loop-safe: a failed re-read
				// writes the same participation values, so this effect does not re-run.
				if (!cancelled && loadedAccessRequest?.status === ACCESS_REQUEST_STATUS.ENROLLED) {
					onEnrolled();
				}
			} catch (error) {
				// The banner is informational, so an unreachable endpoint hides it rather than
				// failing the whole Inventory page - see PartnerPreviewBanner's identical rationale.
				setAccessRequest(null);
			} finally {
				setLoading(false);
			}
		};

		loadAccessRequest();

		return () => {
			cancelled = true;
		};
	}, [participationLoaded, participating]);

	const handleRequestAccess = async () => {
		setSubmitting(true);

		try {
			const updatedAccessRequest = await partnerPreviewService.requestAccess();
			setAccessRequest(updatedAccessRequest);

			// The company was enrolled while this page was open: the banner turns into the enrolled
			// one, so say why instead of letting the click look ignored. A pending result needs no
			// toast - the pending banner already says what happened, including for a stale page
			// where a colleague requested first. onEnrolled makes the page re-read the
			// participation, so the side-pane toggle, the Version Type column and the row icons
			// appear as well, matching what the enrolled banner tells the user.
			if (updatedAccessRequest.status === ACCESS_REQUEST_STATUS.ENROLLED) {
				pushSuccessfulNotification(ALREADY_ENROLLED_MESSAGE);
				onEnrolled();
			}
		} catch (error) {
			pushDangerNotification(
				typeof error === 'string' ? error : error?.response?.data?.detail || REQUEST_ACCESS_FAILED_MESSAGE
			);
		} finally {
			setSubmitting(false);
		}
	};

	if (!participationLoaded) {
		return null;
	}

	if (participating) {
		return renderEnrolledBanner();
	}

	if (loading || !accessRequest) {
		return null;
	}

	if (accessRequest.status === ACCESS_REQUEST_STATUS.ENROLLED) {
		return renderEnrolledBanner();
	}

	if (accessRequest.status === ACCESS_REQUEST_STATUS.PENDING) {
		return (
			<Alert variant="primary" className="mb-3">
				<div className="d-flex justify-content-between align-items-center">
					<div>
						<div className="fw-bold">Your request to join the Partner Preview Program has been sent.</div>
						<div>
							{`Requested by ${accessRequest.requestedBy.name} on ${moment(accessRequest.requestedAt).format(DATE_FORMAT)}. Syneto Support will contact you.`}
						</div>
					</div>
					<div className="d-flex align-items-center flex-shrink-0 text-nowrap ms-4">
						<Button disabled>Access requested</Button>
					</div>
				</div>
			</Alert>
		);
	}

	if (accessRequest.status !== ACCESS_REQUEST_STATUS.NOT_REQUESTED) {
		return null;
	}

	return (
		<Alert variant="primary" className="mb-3">
			<div className="d-flex justify-content-between align-items-center">
				<div>
					<div className="fw-bold">Join the Partner Preview Program.</div>
					<div>Get early access to SynetoOS Preview builds and help us validate new releases before General Availability.</div>
				</div>
				<div className="d-flex align-items-center flex-shrink-0 text-nowrap ms-4">
					<Button onClick={handleRequestAccess} disabled={submitting}>Request access</Button>
				</div>
			</div>
		</Alert>
	);
};

PartnerPreviewProgramBanner.propTypes = {
	participating: PropTypes.bool.isRequired,
	participationLoaded: PropTypes.bool.isRequired,
	onEnrolled: PropTypes.func.isRequired,
};
